/**
 * Local rate limiting against Simkl's documented budget.
 *
 * Measured at Gate A: Simkl sends no `Access-Control-Expose-Headers`, so a
 * browser cannot read `Retry-After` or any `X-RateLimit-*` value. There is no
 * server-advised signal to follow, which makes budgeting locally the only
 * option — and makes staying under the limit more important than usual, since
 * we cannot see how close we are.
 */

/** Documented Simkl ceilings, per client id and per token. */
export const GET_PER_SECOND = 10;
export const POST_PER_SECOND = 1;

/** Leave headroom; hitting the exact ceiling is how you discover clock skew. */
const SAFETY = 0.8;

export interface Clock {
  now: () => number;
  sleep: (ms: number) => Promise<void>;
}

export const systemClock: Clock = {
  now: () => Date.now(),
  sleep: (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    }),
};

/**
 * Serialises calls so no more than `perSecond` start within any one second.
 *
 * Requests queue rather than fail: a burst of season-marking writes should take
 * longer, not drop episodes.
 */
export class RateLimiter {
  readonly #minGapMs: number;
  readonly #clock: Clock;
  #tail: Promise<void> = Promise.resolve();
  #lastStart = 0;

  constructor(perSecond: number, clock: Clock = systemClock) {
    if (perSecond <= 0) throw new RangeError("perSecond must be positive");
    this.#minGapMs = 1000 / (perSecond * SAFETY);
    this.#clock = clock;
  }

  /** Runs `task` once a slot is free, preserving call order. */
  async run<T>(task: () => Promise<T>): Promise<T> {
    const slot = this.#tail.then(async () => {
      const wait = this.#lastStart + this.#minGapMs - this.#clock.now();
      if (wait > 0) await this.#clock.sleep(wait);
      this.#lastStart = this.#clock.now();
    });
    // Keep the chain alive even when a task throws, or one failure stalls
    // every queued request behind it.
    this.#tail = slot.catch(() => {});
    await slot;
    return task();
  }
}

/** Full jitter backoff: 2^attempt seconds, randomised, capped. */
export function backoffMs(attempt: number, random: () => number = Math.random): number {
  const ceiling = Math.min(2 ** attempt * 1000, 16_000);
  return Math.round(random() * ceiling);
}
